import React, { useEffect, useMemo, useRef } from "react";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { RiArrowDropDownLine } from "react-icons/ri";
import BoxPayGrid from "../BoxPayGrid";
import { AllReportschema } from "./AllReportschema";
import ReportModal from "./reportModal";
import { useState } from "react";
import { useAuth } from "../../context/API_Client";
import InputSearchField from "./searchFieldComponent";
import StatusModal from "./statusModal";
import FileSaver from "file-saver";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import ContentHeader from "../../reusableComponents/contentHeader/ContentHeader";
import { customToast } from "../../reusableComponents/customToaster/customToast";
import { readableColor } from "polished";
import { colorBrandObject } from "../../../constants/brandings";
import { PAGE_LIST, sortArrayValue } from "../utils/utils";

export default function ReportsGenerated({ reportData, operationPermissions }) {
  const { getAllReportType, getAllVisualizationData, getDownloadPSPReport, getAllReportStatus } =
    useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleStatusModalOpen = () => setStatusOpen(true);
  const handleStatusModalClose = () => setStatusOpen(false);
  const [reportTypeData, setReportTypeData] = useState([]);
  const [statusTypeData, setStatusTypeData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [statusFilterData, setStatusFilterData] = useState([]);
  const [filteredReportData, setFilteredReportData] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageLimit, setPageLimit] = useState(PAGE_LIST[2]);
  const [totalDataCount, setTotalDataCount] = useState(100);
  const firstUpdateRef = useRef(true);

  const handleFilteredData = (filterData) => {
    setFilterData(filterData);
  };
  const handleFilteredStatusData = (statusFilterData) => {
    setStatusFilterData(statusFilterData);
  };
  useEffect(() => {
    getAllReportType(
      function (data) {
        let filteredReportTypeOptions = []
        if (data?.length) {
          filteredReportTypeOptions = sortArrayValue(data, "reportTitle")
        }
        setReportTypeData(filteredReportTypeOptions);
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    );
    getAllReportStatus(
      function (data) {
        const filterStatusTypes = data?.map(ele => ele?.statuses)
        setStatusTypeData(filterStatusTypes)
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    )
  }, []);
  useEffect(() => {
    if (reportData) {
      setFilteredReportData(reportData);
    }
  }, [reportData]);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  let toDate = new Date();
  toDate.setHours(23, 59, 59, 999);
  let fromDate = new Date(toDate);
  fromDate.setMonth(fromDate.getMonth() - 13);
  fromDate.setHours(0, 0, 0, 0);
  function getFilteredData(attrName, values) {
    let search = [];
    if (Array.isArray(values) || values?.length > 0) {
      search.push({
        attrName: attrName,
        operator: "containsIgnoreCase",
        values: [values],
      });
    }
    setLoading(true);
    const attributeFilters = {};
    if (filterData?.length) {
      setLoading(true);
      attributeFilters.reportType = filterData;
    }
    if (statusFilterData?.length) {
      setLoading(true);
      attributeFilters.status = statusFilterData;
    }
    getAllVisualizationData(
      "merchant_reports",
      {
        fromDate: fromDate,
        toDate: toDate,
        limit: pageLimit,
        offset: (pageNo - 1) * pageLimit,
        sort: {
          addedTimestamp: "desc",
        },
        attributeFilters: attributeFilters,
        advancedAttributeFilters: search,
      },
      function (data) {
        setFilteredReportData(data?.points);
        setLoading(false);
        setTotalDataCount(data?.totalCount);
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
        setLoading(false);
      }
    );
  }

  const memoizedFilterData = useMemo(() => filterData, [filterData])
  const memoizedStatusFilterData = useMemo(() => statusFilterData, [statusFilterData])
  useEffect(() => {
    if (firstUpdateRef.current) {
      firstUpdateRef.current = false;
      return
    }
    getFilteredData("", "");
  }, [memoizedFilterData, memoizedStatusFilterData, pageNo, pageLimit]);

  const handleSearchData = (attrName, values) => {
    setPageNo(1);
    getFilteredData(attrName, values);
  };
  const [updatedReportData, setUpdatedReportData] = useState([]);
  useEffect(() => {
    const updatedData = filteredReportData?.map((data) => {
      const matchedType = reportTypeData?.find(
        (type) => type.reportType === data.reportType
      );
      if (matchedType) {
        return {
          ...data,
          reportTitle: matchedType.reportTitle,
        };
      }
      return data;
    });
    setUpdatedReportData(updatedData);
  }, [filteredReportData, reportTypeData]);
  const toastState = () => {
    customToast("success", "high", "Download successful");
  };
  const handleDownload = (url) => {
    getDownloadPSPReport(
      url,
      function (data) {
        const contentDisposition = data.headers["content-disposition"];
        const match = contentDisposition.match(/filename="(.+?)"/);
        const contentType = data.headers["content-type"];
        let filename;
        if (match && match[1]) {
          filename = match[1];
        } else {
          filename = "Data.csv";
        }
        const blob = new Blob([data.data], {
          type: contentType,
        });
        FileSaver.saveAs(blob, filename);
        toastState();
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    );
  };

  return (
    <>
      <div className="content-wrapper" style={{ backgroundColor: "white" }}>
        <section className="content">
          <div className="reportMainDiv">
            <ContentHeader
              firstHeading="Reports"
              secondHeading="Generate reports for all your business transactions, settlements, payment links & subscription in one place"
              buttonPermissions={[operationPermissions?.createReport]}
              icon={<SummarizeOutlinedIcon sx={{ color: colorBrandObject.sideBarSelectedOptionBackground }} />}
              buttonText="Generate Report"
              buttonType="primary"
              buttonSize="md"
              buttonVariant="contained"
              buttonOnclick={() => navigate("/reports/createReport")}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "24px",
                width: "100%",
              }}
            >
              <div style={{ width: "70%" }}>
                <InputSearchField searchData={handleSearchData} />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                marginTop: "32px",
              }}
            >
              <div>
                <Button
                  onClick={() => handleOpen()}
                  endIcon={<RiArrowDropDownLine className="modalCrossIcon" />}
                  style={{
                    borderRadius: "44px",
                    padding: "16px",
                    border: "2px solid black",
                    color: "rgba(1, 1, 2, 0.72)",
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "poppins",
                    textTransform: "none",
                    marginRight: "12px",
                    border: "1px solid rgba(1, 1, 2, 0.72)",
                    width: "fit-content",
                    height: "40px",
                  }}
                >
                  {filterData?.length > 0 && (
                    <div className="countValueDiv" style={{
                      color: readableColor(colorBrandObject?.secondaryButtonColor),
                      backgroundColor: colorBrandObject?.secondaryButtonColor
                    }}>{filterData?.length}</div>
                  )}
                  Report Type
                </Button>
                <ReportModal
                  openModal={open}
                  onClose={handleClose}
                  sendDataToReportGenerated={handleFilteredData}
                  reportTypeData={reportTypeData}
                />
              </div>
              <div>
                <Button
                  onClick={() => handleStatusModalOpen()}
                  endIcon={<RiArrowDropDownLine className="modalCrossIcon" />}
                  style={{
                    borderRadius: "44px",
                    padding: "16px",
                    border: "2px solid black",
                    color: "rgba(1, 1, 2, 0.72)",
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "poppins",
                    textTransform: "none",
                    marginRight: "12px",
                    border: "1px solid rgba(1, 1, 2, 0.72)",
                    width: "fit-content",
                    height: "40px",
                  }}
                >
                  {statusFilterData?.length > 0 && (
                    <div className="countValueDiv" style={{
                      color: readableColor(colorBrandObject?.secondaryButtonColor),
                      backgroundColor: colorBrandObject?.secondaryButtonColor
                    }}>
                      {statusFilterData?.length}
                    </div>
                  )}
                  Status
                </Button>
                <StatusModal
                  openModal={statusOpen}
                  onClose={handleStatusModalClose}
                  sendDataToReportGenerated={handleFilteredStatusData}
                  statusTypeData={statusTypeData}
                />
              </div>
            </div>
            <div style={{ marginTop: "32px", width: "100%", display: "flex" }}>
              <BoxPayGrid
                schema={AllReportschema(handleDownload, operationPermissions)}
                data={updatedReportData}
                loader={loading}
                showCustomizeButton={false}
                customPagination={true}
                pageNo={pageNo}
                setPageNo={setPageNo}
                pageLimit={pageLimit}
                setPageLimit={setPageLimit}
                totalDataCount={totalDataCount}
              />

              {/* {loading ? (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    marginTop: "24px",
                    justifyContent: "center",
                    alignItems: "center",
                    height: loading ? "50vh" : "auto",
                  }}
                >
                  <LoadingLogoPage />
                </div>
              ) : (
                <BoxPayGrid
                  schema={AllReportschema(handleDownload, operationPermissions)}
                  data={updatedReportData}
                  loader={loading}
                  showCustomizeButton={false}
                  customPagination={true}
                  pageNo={pageNo}
                  setPageNo={setPageNo}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                  totalDataCount={totalDataCount}
                />
              )} */}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
